import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import Combobox from '@/components/Combobox';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import type { ApiPaths, ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getPath } from '@/lib/api/path';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
const MASTER_DATA_API_PATHS = ['/v2/grades', '/v2/job-titles', '/v2/job-types', '/v2/job-functions', '/v2/job-levels', '/v2/job-competencies', '/v2/job-grades', '/v2/work-modes', '/v2/places', '/v2/employee-sub-id-names', '/v2/positions'] as const satisfies readonly ApiPaths[];
export type MasterDataApiPaths = (typeof MASTER_DATA_API_PATHS)[number];
type GradeListResponse = ApiResponseBody<'/v2/grades', 'get'>;
type MasterDataListItem = Pick<GradeListResponse['items'][number], 'id' | 'name'>;
type MasterDataListResponse = {
  items: MasterDataListItem[];
  page: GradeListResponse['page'];
  maxPage: GradeListResponse['maxPage'];
};
export type MasterDataComboboxProps = {
  value?: string;
  displayValue?: string;
  onChange: (value: string | null, label?: string) => void;
  apiPath: MasterDataApiPaths;
  queryKeyPrefix: string;
  disabled?: boolean;
  className?: string;
};
export const MasterDataCombobox = (props: MasterDataComboboxProps) => {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.masterData'
  });
  const [open, setOpen] = useState(false);
  const popoverId = useId();
  const [searchText, setSearchText] = useState('');
  const debouncedSetSearchText = useDebouncedCallback(setSearchText, []);
  const valueToLabelCache = useValueToLabelCache({
    base: props.value && props.displayValue ? {
      [props.value]: props.displayValue
    } : undefined
  });
  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage
  } = useInfiniteQuery<MasterDataListResponse, Error>({
    queryKey: [getPath(props.apiPath), {
      searchText
    }],
    queryFn: async ({
      pageParam
    }) => {
      const res = await apiClient.GET(props.apiPath as Extract<MasterDataApiPaths, string>, {
        params: {
          query: {
            freeText: searchText,
            page: pageParam as number,
            pageSize: 20
          }
        } as Record<string, unknown>
      });
      const raw = res.data as MasterDataListResponse | undefined;
      return {
        items: raw?.items ?? [],
        maxPage: raw?.maxPage ?? 1,
        page: raw?.page ?? 1
      };
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      return lastPage.page < lastPage.maxPage ? lastPage.page + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000
  });
  const options = useMemo(() => {
    return data?.pages.flatMap(page => page.items.map((i: {
      id: string;
      name: string;
    }) => ({
      value: i.id,
      label: i.name
    }))) || [];
  }, [data]);
  const label = useMemo(() => {
    if (!props.value) return '';
    const cached = valueToLabelCache.get(props.value);
    if (cached) return cached;
    const found = options.find(o => o.value === props.value);
    if (found) return found.label;
    return props.displayValue || '';
  }, [props.value, props.displayValue, options, valueToLabelCache]);
  useEffect(() => {
    if (!open) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
      setSearchText('');
    }
  }, [open]);
  return <Popover open={open} onOpenChange={() => setOpen(!open)} modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverId} className={cn('justify-between overflow-hidden text-ellipsis w-full', !props.value && 'text-neutral-muted-on-fill', props.className)} disabled={props.disabled}>
          <span className="min-w-0 truncate">
            {label || tc('pleaseSelect')}
          </span>
          <Icon iconName="ChevronDownIcon" className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent id={popoverId} className="w-[300px] p-0 z-50" align="start">
        {isError ? <div className="p-4 text-center">
            <Text variant="pSmall" className="text-error-on-neutral-fill">
              {t('loadFailed')}
            </Text>
          </div> : <Combobox type="single" value={props.value ?? null} selectionDisplayStyle="hidden" onValueChange={val => {
        const option = options.find(o => o.value === val);
        if (option?.value && option?.label) {
          valueToLabelCache.set(option.value, option.label);
        }
        props.onChange(val, option?.label);
        setOpen(false);
      }} isPending={isLoading} isFetching={isFetchingNextPage} options={options} onScrollToEnd={fetchNextPage} onSearchChange={debouncedSetSearchText} search={searchText} valueToLabelCache={valueToLabelCache} />}
      </PopoverContent>
    </Popover>;
};